import { useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, useTheme, colors } from '@iq/react-components';

import PopoutSelect from '../PopoutSelect';
import {
  getStaticComponents,
  getComponentsLoaded,
  requestComponents,
} from '../../bundles/components';
import { getActiveSiteId } from '../../bundles/sites';
import Loader from '../Loader';
import { useMemoizedDeepComparison } from '../../utils';

const compSort = (items) =>
  items?.toSorted((a, b) => {
    const { name: nameA, itemDesignation: desA } = a;
    const { name: nameB, itemDesignation: desB } = b;
    return (desA + nameA).localeCompare(desB + nameB);
  });

export default function ComponentSelect({
  label,
  icon,
  isMulti = true,
  onChange = () => null,
  value,
  popout = false,
  placeholder,
  availableComponents,
  menuXPlacement = 'right',
  menuYPlacement = 'auto',
  menuMaxHeight,
  fullHeight = false,
  useAssetIds = false,
  nullOptionText = '',
  customFilter = () => true,
  styles = {},
  ...properties
}) {
  const SelectComponent = popout ? PopoutSelect : Select;

  const dispatch = useDispatch();
  const theme = useTheme();
  const siteId = useSelector(getActiveSiteId);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const components = useSelector(getStaticComponents);

  useEffect(() => {
    if (siteId && !componentsLoaded) {
      dispatch(requestComponents(siteId));
    }
  }, [siteId, componentsLoaded, dispatch]);

  const memoizedComponents = useMemoizedDeepComparison(components);
  const sortedComponents = useMemo(() => compSort(memoizedComponents), [memoizedComponents]);
  const sortedAvailableComponents = useMemo(
    () => compSort(availableComponents),
    [availableComponents]
  );

  const overrideStyles = () => ({
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? colors.Grey10 : base.color,
    }),
    menu: (base) => ({
      ...base,
      ...(fullHeight ? {} : { minHeight: 'unset' }),
      border: `1px solid ${theme === 'dark' ? colors.Grey70 : colors.Grey20}`,
      marginTop: '0.2rem',
      zIndex: '1000',
    }),
    control: (base) => ({
      ...base,
      lineHeight: '1.3rem',
      backgroundColor: `${theme === 'dark' ? colors.Grey90 : colors.Grey0}`,
      ...styles.control,
    }),
    clearIndicator: (base) => ({
      ...base,
      ...styles.clearIndicator,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      ...styles.dropdownIndicator,
    }),
    menuList: (base) => ({
      ...base,
      ...(menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
      scrollbarColor: `${
        theme === 'dark'
          ? 'rgba(255, 255, 255, .25) rgba(0, 0, 0, 0)'
          : 'rgba(0, 0, 0, .25) rgba(0, 0, 0, 0)'
      }`,
      '::-webkit-scrollbar': {
        width: '7px',
      },
      '::-webkit-scrollbar-thumb': {
        background: `${theme === 'dark' ? 'rgba(255, 255, 255, .25)' : 'rgba(0, 0, 0, .25)'}`,
        borderRadius: '7px',
        boxShadow: `${
          theme === 'dark' ? '0 0 1px rgba(0, 0, 0, .5)' : '"0 0 1px rgba(255, 255, 255, .5)"'
        }`,
      },
    }),
  });

  const componentOptions = useMemo(() => {
    const options = [];
    if (!isMulti && nullOptionText) {
      options.push({
        value: null,
        label: nullOptionText,
      });
    }
    options.push(
      ...(sortedAvailableComponents || sortedComponents)
        .filter((c) => customFilter(c))
        .map((c) => ({
          value: useAssetIds ? c.asset?.id : c.id,
          label: `[ ${c.itemDesignation} ] ${c.name}`,
        }))
    );
    return options;
  }, [
    isMulti,
    nullOptionText,
    sortedComponents,
    sortedAvailableComponents,
    customFilter,
    useAssetIds,
  ]);

  const handleOnChange = useCallback(
    (selection, change) => {
      if (change.action === 'clear') {
        onChange(isMulti ? [] : null);
        return;
      }
      if (isMulti) {
        onChange(selection ? selection.map((option) => option.value) : []);
      } else {
        onChange(selection ? selection.value : null);
      }
    },
    [onChange, isMulti]
  );

  const localValue = useMemo(() => {
    if (isMulti) {
      return componentOptions?.filter((option) => (value || []).find((id) => option.value === id));
    }
    return componentOptions?.find((option) => option.value === value);
  }, [value, componentOptions, isMulti]);

  if (!componentOptions) {
    return <Loader />;
  }

  return (
    <SelectComponent
      label={popout && label}
      icon={popout && icon}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      onChange={handleOnChange}
      value={localValue}
      options={componentOptions}
      placeholder={placeholder || 'Search...'}
      menuXPlacement={menuXPlacement}
      menuYPlacement={popout ? 'bottom' : menuYPlacement}
      styles={(menuMaxHeight || !fullHeight) && overrideStyles()}
      {...properties}
    />
  );
}
